import { observable, action, computed, runInAction, ObservableMap, makeObservable } from "mobx";
import { API } from "General/API";
import { mapInfoBoxProps, mapActionButtonProps } from "./helpers";
import { titleStore } from "Stores/TitleStore";
import { handleStoreState } from "Stores/RESTStore/CoreStore";
import { MapDataStore } from "./RESTStoreInstances/MapDataStore";
import { API_URL } from "General/APIEndpoints";
import { userInfoStore } from "./UserInfoStore";
import { featureFlagsStore } from "./FeatureFlagsStore";
import { lastActionsStore } from "Pages/Dashboard/Stores/LastActionsStore";
import { FileUploadChunked } from "Pages/Project/Documents/FileUpload/FileUploadChunked";
import { orderBy } from "lodash";
import { userSettingsStore } from "./UserSettingsStore";

/**
 * Utility method, extends and normalizes received data from the server to render icons and labels.
 * @param project - received projects list from the server
 */
function normalizeProjectsList(project: API_Project) {
  if (project && project.modules) {
    return {
      ...project,
      modules: mapInfoBoxProps(project.modules),
      actions: mapActionButtonProps(project.modules),
    };
  }
  return project;
}

export class ProjectsDataStore extends MapDataStore<number, ProjectItemUI> {
  projectId: number = null;
  selectedProjectIdOnDashboard: number = null;
  sorting: { order: "asc" | "desc"; namespace: string } = { order: "asc", namespace: "name" };
  searchQuery: string = "";
  aiChatSettingsAvailable: boolean = false;

  constructor() {
    /**
     * Optional parameter for the heirs of CoreStore
     * @param options - object with different options
     * @param options.omitAllStoresArray - boolean value for omit current store
     */
    super({ omitAllStoresArray: false, id: "id" });

    makeObservable(this, {
      projectId: observable,
      selectedProjectIdOnDashboard: observable,
      setSelectedProjectIdOnDashboard: action,
      sorting: observable,
      searchQuery: observable,
      aiChatSettingsAvailable: observable,
      setProjectId: action.bound,
      resetProjectId: action.bound,
      fetchCurrentProject: action.bound,
      activeProjectsList: computed,
      archivedProjects: computed,
      showDisclaimer: computed,
      verificationRequired: computed,
      filenameConvDoc: computed,
      passwordRequired: computed,
      isAdmin: computed,
      currentProject: computed,
      userRole: computed,
      isWFAdmin: computed,
      currentModules: computed,
      functions: computed,
      isArchived: computed,
      printJobSettings: computed,
      labelsSettings: computed,
      projectSettings: computed,
      searcResultsByActiveProjects: computed,
      searcResultsByArchivedProjects: computed,
      sortBy: action.bound,
      searchByParam: action.bound,
      setSearchParam: action.bound,
      setVerificationIsRequired: action.bound,
      list: computed,
      loadErrorProjects: computed,
      getCurrentProject: action.bound,
      acceptDisclaimer: action.bound,
      updateProperty: action.bound,
      checkAiChatSettingsAvailable: action.bound,
    });

    this.processResponse = normalizeProjectsList;
    this.sorting = { order: "asc", namespace: "name" };
    this.searchQuery = "";
    this.data = new Map() as ObservableMap<number, ProjectItemUI>;
  }

  mapKey(project: API_Project) {
    return project.id;
  }

  url(id?: number) {
    if (!id) {
      return API_URL.PROJECTS.getProjectsOfUser();
    } else {
      return API_URL.PROJECTS.getProject_1(id);
    }
  }

  setProjectId(id: number) {
    this.projectId = id;
  }

  setSelectedProjectIdOnDashboard(projectId: number): void {
    if (projectId === this.selectedProjectIdOnDashboard) {
      this.selectedProjectIdOnDashboard = -1;
      lastActionsStore.viewMode = "ALL";
    } else {
      if (lastActionsStore.viewMode === "ALL") {
        lastActionsStore.viewMode = "PROJECT";
      }
      this.selectedProjectIdOnDashboard = projectId;
    }
  }

  resetProjectId() {
    this.projectId = undefined;
  }

  @handleStoreState()
  async fetchCurrentProject(forceFetch = false, newAccess = false) {
    let project: ProjectItemUI;
    const fetchedProject = await API.getData(this.url(this.projectId), {
      force: forceFetch,
      newAccess,
    });
    if (fetchedProject.active && !fetchedProject.archivProject) {
      FileUploadChunked.fetchConstants(this.projectId);
    }
    runInAction(() => {
      project = normalizeProjectsList(fetchedProject) as ProjectItemUI;
    });

    runInAction(() => {
      this.data.set(fetchedProject.id, { ...project });
    });
    return project;
  }

  @handleStoreState()
  async getCurrentProject(forceUpdate = false, newAccess = false) {
    let project: ProjectItemUI;
    project = this.find(this.projectId) as ProjectItemUI;

    if (!project || forceUpdate) {
      project = await this.fetchCurrentProject(forceUpdate, newAccess);
      await this.checkAiChatSettingsAvailable(project);
    }

    await featureFlagsStore.fetchForProject(this.projectId);

    runInAction(() => {
      if (project) {
        titleStore.setProjectName(project.name);
        this.edit(project.id, project);
      }
    });

    return project;
  }

  getCurrentProjectEmailSignature() {
    return userSettingsStore.getEmailSignature(this.projectId);
  }

  get activeProjectsList(): ProjectItemUI[] {
    return this.searchQuery
      ? this.searcResultsByActiveProjects
      : this.list.filter((item: ProjectItemUI) => item.archivProject === false);
  }

  get archivedProjects(): ProjectItemUI[] {
    return this.searchQuery
      ? this.searcResultsByArchivedProjects
      : this.list.filter((item: ProjectItemUI) => item.archivProject === true);
  }

  @handleStoreState()
  async acceptDisclaimer() {
    this.find(this.projectId).disclaimer = false;
  }

  get showDisclaimer() {
    const foundProject = this.find(this.projectId);
    return foundProject && foundProject.disclaimer;
  }

  get verificationRequired() {
    return this.currentProject && !!this.currentProject.verificationRequired;
  }

  get filenameConvDoc() {
    return this.currentProject?.filenameConventionDocument;
  }

  get passwordRequired() {
    return this.currentProject && !!this.currentProject.passwordRequired;
  }

  get isAdmin() {
    return this.find(this.projectId) && this.find(this.projectId).userrole === "ADMIN";
  }

  get isArchiveAdmin() {
    return this.find(this.projectId)?.userrole === "ARCHIVE_ADMIN";
  }

  get currentProject() {
    const project = this.find(this.projectId);
    if (project && !project.disclaimer) {
      return project;
    } else {
      return null;
    }
  }

  updateProperty<K extends keyof ProjectItemUI>(property: K, value: ProjectItemUI[K]): void {
    const project = this.find(this.projectId);
    if (project) {
      project[property] = value;
    }
  }

  get userRole() {
    return this.currentProject.userrole;
  }

  get isWFAdmin() {
    return this.currentProject.hasWorkflowAdminRights;
  }

  get currentModules(): API_MODULEDATA_MODULE[] | undefined {
    const forRestrictedUser = !userInfoStore.allowedToChangeSetting;
    if (this.currentProject) {
      if (forRestrictedUser) {
        // restricted users are not allowed to use investigations module.
        return this.currentProject.modules.reduce((modules, item) => {
          if (item.module !== "INVESTIGATIONS") {
            modules.push(item.module);
          }
          return modules;
        }, []);
      } else {
        return this.currentProject.modules.map((val) => val.module as API_PROJECT_STARTWITH);
      }
    } else {
      return undefined;
    }
  }

  get functions() {
    return this.currentProject && this.currentProject.functions;
  }

  get isArchived() {
    return this.currentProject && this.currentProject.archivProject;
  }

  get printJobSettings() {
    return this.currentProject && this.currentProject.printJobModuleSettings;
  }

  get labelsSettings() {
    return this.currentProject && this.currentProject.labelOptions;
  }

  get projectSettings() {
    return this.currentProject && this.currentProject.projectSettings;
  }

  get bcfGuid() {
    return this.currentProject && this.currentProject.guid;
  }

  get bcfServer() {
    return this.currentProject && this.currentProject.bcfServer;
  }

  isQRForce() {
    return this.currentProject && this.currentProject.projectSettings && this.currentProject.projectSettings.qrForce;
  }

  isQrMDFilter() {
    return this.currentProject && this.currentProject.projectSettings && this.currentProject.projectSettings.qrMDFilter;
  }

  get searcResultsByActiveProjects(): ProjectItemUI[] {
    const ret = orderBy(
      this.searchByParam(this.searchQuery).filter((item: ProjectItemUI) => item.archivProject === false),
      [this.sorting.namespace],
      [this.sorting.order]
    );
    if (lastActionsStore?.viewMode === "PROJECT" && ret.length > 0) {
      runInAction(() => {
        this.selectedProjectIdOnDashboard = ret[0].id;
      });
    }
    return ret;
  }

  get searcResultsByArchivedProjects() {
    return orderBy(
      this.searchByParam(this.searchQuery).filter((item: ProjectItemUI) => item.archivProject === true),
      [this.sorting.namespace],
      [this.sorting.order]
    );
  }

  sortBy(order: "asc" | "desc", namespace: string = this.sorting.namespace) {
    if (order !== this.sorting.order || namespace !== this.sorting.namespace) {
      this.sorting.order = order;
      this.sorting.namespace = namespace;
    }
  }

  searchByParam(searchQuery?: string): ProjectItemUI[] {
    const data = Array.from(this.data.values()) as ProjectItemUI[];
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return data.filter((item: ProjectItemUI) => {
        return item.name.toLowerCase().indexOf(query) !== -1 || item.id.toString().indexOf(query) !== -1;
      });
    }

    return data;
  }

  setSearchParam(query?: string) {
    this.searchQuery = query;
  }

  setVerificationIsRequired() {
    const project = this.data.get(this.projectId);
    this.data.set(this.projectId, { ...project, verificationRequired: true });
  }

  async checkAiChatSettingsAvailable(project: ProjectItemUI) {
    const currentProject = project || this.currentProject;
    let aiChatSettingsAvailable = false;
    if (currentProject.functions.includes("AI_CHAT") && this.isAdmin) {
      const response = await API.getData<API_AiChatSettings>(API_URL.PROJECTS.getAiChatSettings(currentProject.id));
      if (response.aiChatAsSettings) {
        aiChatSettingsAvailable = true;
      } else {
        aiChatSettingsAvailable = false;
      }
    } else {
      aiChatSettingsAvailable = false;
    }
    this.aiChatSettingsAvailable = aiChatSettingsAvailable;
  }

  get list(): ProjectItemUI[] {
    return orderBy(Array.from(this.data.values()), this.sorting.namespace, this.sorting.order);
  }

  get loadErrorProjects(): ProjectItemUI[] {
    return this.list.filter((project) => project.error);
  }

  /* Condition for showing the synchronization tools tab */
  get hasSomeSyncTools() {
    return this.currentProject.functions.some(
      (item) =>
        item === "SYNCHRONIZATION_BOX" ||
        item === "SYNCHRONIZATION_PROBIS" ||
        item === "SYNCHRONIZATION_BIM_COLLAB" ||
        (item === "AI_CHAT" && this.aiChatSettingsAvailable)
    );
  }
}

export const projectsDataStore = new ProjectsDataStore();
